const baseTheme = {
    colors: {
        primary: '#ff0000ff', // Pure red primary color
        primaryLight: "rgba(255, 0, 0, 0.7)", // Light red
        primaryDark: "rgba(200, 0, 0, 0.9)", // Dark red
        secondary: '#ff6b6b', // Complementary coral red
        error: '#f44336',
        success: '#4caf50',
        warning: '#ff9800',
        info: '#ff4444', // Red-based info color
        darkRed: '#CC0000', // Darker red
        darkGreen: '#2e7d32',
        darkBlue: '#1a1a1a', // Dark neutral instead of blue
        darkOrange: '#ef6c00',
        // Gradient variations based on red primary
        primaryGradient: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
        primaryGradientHover: 'linear-gradient(135deg, #cc0000 0%, #990000 100%)',
        primaryGradientLight: 'linear-gradient(135deg, rgba(255, 0, 0, 0.1) 0%, rgba(204, 0, 0, 0.1) 100%)',
        primaryGradientReverse: 'linear-gradient(135deg, #cc0000 0%, #ff0000 100%)',
        // Alternative gradient variations
        primaryGradient45: 'linear-gradient(45deg, #ff0000 0%, #cc0000 100%)',
        primaryGradientVertical: 'linear-gradient(to bottom, #ff0000 0%, #cc0000 100%)',
        primaryGradientRadial: 'radial-gradient(circle, #ff0000 0%, #cc0000 100%)',
        // Solid color extracts from gradient
        primaryPink: '#ff0000', // Pure red (renamed for consistency)
        primaryBlue: '#cc0000', // Dark red (renamed for consistency)
        primaryPinkDark: '#cc0000',
        primaryBlueDark: '#990000',
        primaryPinkLight: 'rgba(255, 0, 0, 0.5)',
        primaryBlueLight: 'rgba(204, 0, 0, 0.5)'
    },
    fontSizes: {
        xs: '0.75rem',
        sm: '0.875rem',
        md: '1rem',
        lg: '1.125rem',
        xl: '1.25rem',
        xxl: '1.5rem',
        xxxl: '2rem',
    },
    breakpoints: {
        xs: '480px',
        sm: '768px',
        md: '1024px',
        lg: '1280px',
        xl: '1440px',
    },
    spacing: {
        xs: '0.25rem',
        sm: '0.5rem',
        md: '1rem',
        lg: '1.5rem',
        xl: '2rem',
        xxl: '3rem',
    },
    shadows: {
        sm: '0 2px 8px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.08)',
        md: '0 4px 12px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)',
        lg: '0 12px 24px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.08)',
        xl: '0 20px 40px rgba(0,0,0,0.12), 0 8px 16px rgba(0,0,0,0.08)',
    },
    borderRadius: {
        sm: '20px',
        md: '30px',
        lg: '50px',
        input: '25px', // Specific for input fields like search bars
        round: '50%',
    },
    zIndex: {
        base: 1,
        header: 100,
        dropdown: 200,
        overlay: 500,
        modal: 2000,
        popover: 2100,
        tooltip: 2200,
        toast: 9999,
        maximum: 2147483646
    },
    socialIcons: {
        instagram: {
            bg: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
            color: '#ffffff'
        },
        twitter: {
            bg: '#000000',
            color: '#ffffff'
        },
        tiktok: {
            bg: '#000000',
            color: '#ffffff'
        },
        website: {
            bg: '#ff0000ff',
            color: '#ffffff'
        },
        facebook: {
            bg: '#1877F2',
            color: '#ffffff'
        },
        youtube: {
            bg: '#FF0000',
            color: '#ffffff'
        },
        twitch: {
            bg: '#9146FF',
            color: '#ffffff'
        },
        snapchat: {
            bg: '#FFFC00',
            color: '#000000'
        },
        threads: {
            bg: '#000000',
            color: '#ffffff'
        },
        pinterest: {
            bg: '#E60023',
            color: '#ffffff'
        },
        spotify: {
            bg: '#1DB954',
            color: '#ffffff'
        },
        discord: {
            bg: '#5865F2',
            color: '#ffffff'
        }
    }
};

export const lightTheme = {
    ...baseTheme,
    colors: {
        ...baseTheme.colors,
        background: 'rgb(255, 255, 255)',
        backgroundAlpha: 'rgba(250, 250, 250, 0.8)',
        backgroundAlt: 'rgba(239, 243, 249, 0.9)',
        bg2: 'rgba(255, 255, 255, 0.85)',
        bg3: 'rgba(230, 230, 230, 0.5)',
        text: 'rgb(45, 45, 45)',
        textPrimary: '#333333',
        textSecondary: '#6e7a8a',
        border: 'rgba(225, 229, 235, 0.3)',
        shadow: 'rgba(15, 15, 15, 0.1)',
        divider: 'rgba(15, 15, 15, 0.06)',
        cardBg: 'rgba(255, 255, 255, 0.95)',
        lightGray: 'rgba(245, 245, 245, 0.9)',
        mediumGray: 'rgba(158, 158, 158, 0.9)',
        darkGray: 'rgba(97, 97, 97, 0.9)',
        danger: '#dc3545',
        dangerDark: '#bd2130',
        dangerLight: 'rgba(220, 53, 69, 0.1)',
    },
    input: {
        bg: 'rgba(255, 255, 255, 0.9)',
        disabled: 'rgba(240, 240, 240, 0.7)'
    },
    button: {
        primaryBg: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
        primaryText: 'rgb(255, 255, 255)',
        primaryHoverBg: 'linear-gradient(135deg, #cc0000 0%, #990000 100%)',
        secondaryBg: 'rgba(240, 240, 240, 0.8)',
        secondaryText: 'rgb(45, 45, 45)',
        secondaryHoverBg: 'rgba(230, 230, 230, 0.9)'
    },
    card: {
        bg: 'rgba(255, 255, 255, 0.9)'
    },
    switch: {
        bg: 'rgba(200, 200, 200, 0.8)'
    }
};

export const darkTheme = {
    ...baseTheme,
    colors: {
        ...baseTheme.colors,
        background: 'rgba(15, 15, 15, 0.95)',
        backgroundAlpha: 'rgba(0, 0, 0, 0.3)',
        backgroundAlt: 'rgba(30, 30, 30, 0.85)',
        bg2: 'rgba(30, 30, 30, 0.85)',
        bg3: 'rgba(50, 50, 50, 0.5)',
        text: 'rgb(210, 210, 210)',
        textPrimary: 'rgb(210, 210, 210)',
        textSecondary: 'rgb(150, 150, 150)',
        border: 'rgba(225, 229, 235, 0.1)',
        shadow: 'rgba(255, 255, 255, 0.1)',
        divider: 'rgba(255, 255, 255, 0.05)',
        cardBg: 'rgba(30, 30, 30, 0.9)',
        lightGray: 'rgba(245, 245, 245, 0.9)',
        mediumGray: 'rgba(158, 158, 158, 0.9)',
        darkGray: 'rgba(97, 97, 97, 0.9)',
        danger: '#dc3545',
        dangerDark: '#bd2130',
        dangerLight: 'rgba(220, 53, 69, 0.1)',
    },
    input: {
        bg: 'rgba(30, 30, 30, 0.9)',
        disabled: 'rgba(50, 50, 50, 0.7)'
    },
    button: {
        primaryBg: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
        primaryText: 'rgb(255, 255, 255)',
        primaryHoverBg: 'linear-gradient(135deg, #cc0000 0%, #990000 100%)',
        secondaryBg: 'rgba(50, 50, 50, 0.8)',
        secondaryText: 'rgb(210, 210, 210)',
        secondaryHoverBg: 'rgba(70, 70, 70, 0.9)'
    },
    card: {
        bg: 'rgba(30, 30, 30, 0.9)'
    },
    switch: {
        bg: 'rgba(70, 70, 70, 0.8)'
    }
};